import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import gameService from '../../services/gameService';
import Modal from '../../components/Modal';
import Pagination from '../../components/Pagination';
import { SearchIcon, EditIcon, TrashIcon, AlertIcon, CheckCircleIcon } from '../../components/Icons';

const emptyCurrencyForm = {
  code: '',
  currency_code: '',
  name: '',
  symbol: '',
  ratio: 1,
  is_active: true
};

const normalizeCurrencyCode = (value) => String(value || '').trim().toUpperCase();

const GameCurrencies = () => {
  const { hasPermission } = useAuth();
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  // Pagination & Search State
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Modal & Form State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editId, setEditId] = useState(null);
  const [originalCurrencyCode, setOriginalCurrencyCode] = useState('');
  const [formError, setFormError] = useState('');
  const [codeCheck, setCodeCheck] = useState({ status: 'idle', message: '' });
  const [formData, setFormData] = useState(emptyCurrencyForm);

  const fetchCurrencies = useCallback(async (page = 1, search = '', limit = 20) => {
    try {
      setLoading(true);
      const response = await gameService.getCurrencies({ page, limit, search });
      if (response.success) {
        setCurrencies(response.data);
        setPagination(prev => ({
          ...prev,
          page: response.pagination.page,
          total: response.pagination.total,
          pages: response.pagination.pages
        }));
      } else {
        setError(response.message || 'Failed to fetch currencies');
      }
    } catch (err) {
      setError('An error occurred while fetching currencies');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchCurrencies(1, searchTerm, pagination.limit);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [fetchCurrencies, pagination.limit, searchTerm]);

  useEffect(() => {
    if (!isModalOpen) return;

    const currencyCode = normalizeCurrencyCode(formData.currency_code);
    if (!currencyCode) {
      setCodeCheck({ status: 'idle', message: '' });
      return;
    }

    if (editId && currencyCode === normalizeCurrencyCode(originalCurrencyCode)) {
      setCodeCheck({ status: 'idle', message: '' });
      return;
    }

    let cancelled = false;
    setCodeCheck({ status: 'checking', message: 'Checking currency code...' });

    const timer = setTimeout(async () => {
      try {
        const response = await gameService.getCurrencies({ page: 1, limit: 10, search: currencyCode });
        if (cancelled) return;

        const duplicateCurrency = (response.data || []).find((currency) => (
          currency._id !== editId &&
          normalizeCurrencyCode(currency.currency_code || currency.code) === currencyCode
        ));

        if (duplicateCurrency) {
          setCodeCheck({
            status: 'duplicate',
            message: `${currencyCode} is already used by ${duplicateCurrency.name || 'another currency'}.`
          });
          return;
        }

        setCodeCheck({ status: 'available', message: `${currencyCode} is available.` });
      } catch (err) {
        if (!cancelled) {
          setCodeCheck({
            status: 'error',
            message: 'Code check is unavailable. Saving will still validate it.'
          });
        }
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [editId, formData.currency_code, isModalOpen, originalCurrencyCode]);

  const canEdit = hasPermission('currencies', 'edit');
  const canDelete = hasPermission('currencies', 'delete');
  const showActions = canEdit || canDelete;
  const normalizedCurrencyCode = normalizeCurrencyCode(formData.currency_code);
  const ratioValue = Number(formData.ratio);
  const isCodeDuplicate = codeCheck.status === 'duplicate';
  const isCheckingCode = codeCheck.status === 'checking';
  const isFormReady = (
    normalizedCurrencyCode &&
    String(formData.name || '').trim() &&
    Number.isFinite(ratioValue) &&
    ratioValue >= 1 &&
    !isCodeDuplicate &&
    !isCheckingCode
  );

  const handlePageChange = (newPage) => {
    fetchCurrencies(newPage, searchTerm, pagination.limit);
  };

  const handleLimitChange = (newLimit) => {
    setPagination(prev => ({ ...prev, limit: newLimit }));
    fetchCurrencies(1, searchTerm, newLimit);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormError('');
    setNotice(null);
    if (name === 'currency_code') {
      const normalized = normalizeCurrencyCode(value);
      setFormData(prev => ({
        ...prev,
        currency_code: normalized,
        code: normalized
      }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? (value === '' ? '' : parseFloat(value)) : value)
    }));
  };

  const openAddModal = () => {
    setEditId(null);
    setOriginalCurrencyCode('');
    setFormData(emptyCurrencyForm);
    setFormError('');
    setCodeCheck({ status: 'idle', message: '' });
    setNotice(null);
    setIsModalOpen(true);
  };

  const openEditModal = (currency) => {
    setEditId(currency._id);
    const resolvedCurrencyCode = currency.currency_code || currency.code || '';
    setOriginalCurrencyCode(resolvedCurrencyCode);
    setFormData({
      currency_code: resolvedCurrencyCode,
      code: currency.code || resolvedCurrencyCode,
      name: currency.name,
      symbol: currency.symbol || '',
      ratio: currency.ratio || 1,
      is_active: currency.is_active
    });
    setFormError('');
    setCodeCheck({ status: 'idle', message: '' });
    setNotice(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormError('');
    setCodeCheck({ status: 'idle', message: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormReady) {
      setFormError(isCodeDuplicate ? 'Choose a different currency code before saving.' : 'Complete the required fields before saving.');
      return;
    }

    try {
      setSubmitting(true);
      setFormError('');
      const payload = {
        ...formData,
        code: normalizedCurrencyCode,
        currency_code: normalizedCurrencyCode,
        name: String(formData.name || '').trim(),
        symbol: String(formData.symbol || '').trim(),
        ratio: ratioValue
      };

      let response;
      if (editId) {
        response = await gameService.updateCurrency(editId, payload);
      } else {
        response = await gameService.createCurrency(payload);
      }

      if (response.success) {
        closeModal();
        setFormData(emptyCurrencyForm);
        setEditId(null);
        setOriginalCurrencyCode('');
        setNotice({
          type: 'success',
          message: `Currency ${normalizedCurrencyCode} ${editId ? 'updated' : 'created'} successfully.`
        });
        fetchCurrencies(pagination.page, searchTerm, pagination.limit);
      } else {
        setFormError(response.message || 'Failed to save currency');
      }
    } catch (err) {
      const serverMsg = err?.response?.data?.message;
      if (/currency code|already exists/i.test(serverMsg || '')) {
        setCodeCheck({
          status: 'duplicate',
          message: serverMsg
        });
        setFormError('Choose a different currency code before saving.');
      } else {
        setFormError(serverMsg || err?.message || 'An error occurred while saving currency');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this currency? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      const response = await gameService.deleteCurrency(id);
      if (response.success) {
        fetchCurrencies(pagination.page, searchTerm, pagination.limit);
      } else {
        alert(response.message || 'Failed to delete currency');
        setLoading(false);
      }
    } catch (err) {
      const serverMsg = err?.response?.data?.message;
      alert(serverMsg || err?.message || 'An error occurred while deleting currency');
      setLoading(false);
    }
  };

  return (
    <div className="game-currencies-page">
      {hasPermission('currencies', 'create') && (
        <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-primary" onClick={openAddModal}>Add Currency</button>
        </div>
      )}

      {notice && (
        <div style={{
          marginBottom: '1rem',
          padding: '0.9rem 1rem',
          borderRadius: 'var(--radius-md)',
          border: `1px solid ${notice.type === 'success' ? 'rgba(16, 185, 129, 0.35)' : 'rgba(239, 68, 68, 0.35)'}`,
          backgroundColor: notice.type === 'success' ? 'rgba(16, 185, 129, 0.08)' : 'rgba(239, 68, 68, 0.08)',
          color: notice.type === 'success' ? 'var(--success)' : 'var(--error)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.65rem',
          fontSize: '0.875rem',
          fontWeight: 600
        }}>
          {notice.type === 'success' ? <CheckCircleIcon size={16} /> : <AlertIcon size={16} />}
          <span>{notice.message}</span>
        </div>
      )}

      <div className="card" style={{ marginBottom: '1.5rem', padding: '1rem' }}>
        <div className="search-container">
          <div className="search-icon-wrapper">
            <SearchIcon />
          </div>
          <input
            type="text"
            className="search-input"
            placeholder="Search code, currency code, or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {loading && currencies.length === 0 ? (
        <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>Loading currencies...</div>
      ) : error ? (
        <div className="card" style={{ padding: '2rem', textAlign: 'center', color: 'var(--error)' }}>{error}</div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto', width: '100%' }}>
            <table className="table-striped" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--table-header-bg)', borderBottom: '1px solid var(--border-light)' }}>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Currency Code</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Name</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Symbol</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Ratio</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Status</th>
                {showActions && <th style={{ padding: '1rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Actions</th>}
              </tr>
            </thead>
            <tbody style={{ opacity: loading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
              {currencies.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>No currencies found.</td>
                </tr>
              ) : (
                currencies.map((currency) => (
                  <tr key={currency._id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', fontWeight: 600 }}>{currency.currency_code || currency.code}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{currency.name}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{currency.symbol || '-'}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                      1 : {currency.ratio}
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                      <span style={{
                        padding: '0.25rem 0.625rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        backgroundColor: currency.is_active ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                        color: currency.is_active ? 'var(--success)' : 'var(--error)'
                      }}>
                        {currency.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {showActions && (
                      <td style={{ padding: '1rem', textAlign: 'right', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                        {canEdit && (
                          <button
                            className="btn"
                            style={{ padding: '0.4rem 0.8rem', fontSize: '0.75rem', color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                            onClick={() => openEditModal(currency)}
                          >
                            <EditIcon size={14} />
                            Edit
                          </button>
                        )}
                        {canDelete && (
                          <button
                            className="btn"
                            style={{ padding: '0.4rem 0.8rem', fontSize: '0.75rem', color: 'var(--error)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                            onClick={() => handleDelete(currency._id)}
                          >
                            <TrashIcon size={14} />
                            Delete
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

          <Pagination
            pagination={pagination}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
            loading={loading}
            itemName="currencies"
          />
        </div>
      )}

      {/* Add/Edit Currency Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editId ? "Edit Currency" : "Add New Currency"}
      >
        <form onSubmit={handleSubmit}>
          {formError && (
            <div style={{
              marginBottom: '1rem',
              padding: '0.85rem 1rem',
              borderRadius: 'var(--radius-md)',
              border: '1px solid rgba(239, 68, 68, 0.35)',
              backgroundColor: 'rgba(239, 68, 68, 0.08)',
              color: 'var(--error)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.6rem',
              fontSize: '0.875rem',
              fontWeight: 600
            }}>
              <AlertIcon size={16} />
              <span>{formError}</span>
            </div>
          )}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>Currency Code (e.g. USD, MYR)</label>
            <input
              type="text"
              name="currency_code"
              className="input"
              value={formData.currency_code}
              onChange={handleInputChange}
              required
              aria-invalid={isCodeDuplicate}
              placeholder="e.g. MYR"
              style={{
                borderColor: isCodeDuplicate ? 'var(--error)' : (codeCheck.status === 'available' ? 'var(--success)' : undefined),
                boxShadow: isCodeDuplicate ? '0 0 0 3px rgba(239, 68, 68, 0.08)' : undefined
              }}
            />
            {codeCheck.message && (
              <div style={{
                marginTop: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                color: codeCheck.status === 'duplicate'
                  ? 'var(--error)'
                  : (codeCheck.status === 'available' ? 'var(--success)' : 'var(--text-muted)'),
                fontSize: '0.78rem',
                fontWeight: 600
              }}>
                {codeCheck.status === 'available' ? <CheckCircleIcon size={14} /> : (codeCheck.status === 'duplicate' ? <AlertIcon size={14} /> : null)}
                <span>{codeCheck.message}</span>
              </div>
            )}
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>Name</label>
            <input
              type="text"
              name="name"
              className="input"
              value={formData.name}
              onChange={handleInputChange}
              required
              placeholder="e.g. Malaysian Ringgit"
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>Symbol</label>
              <input
                type="text"
                name="symbol"
                className="input"
                value={formData.symbol}
                onChange={handleInputChange}
                placeholder="e.g. RM"
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>Ratio (1:X)</label>
              <input
                type="number"
                name="ratio"
                className="input"
                value={formData.ratio}
                onChange={handleInputChange}
                min="1"
                required
                placeholder="e.g. 1000"
              />
            </div>
          </div>
          <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>Is Active</span>
            <label className="switch">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
              />
              <span className="slider"></span>
            </label>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
            <button type="button" className="btn" onClick={closeModal} disabled={submitting}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting || !isFormReady}>
              {submitting ? 'Saving...' : (editId ? 'Update Currency' : 'Create Currency')}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default GameCurrencies;
